class NavBar extends HTMLElement {
	connectedCallback() {
		// One-time init guard: connectedCallback can fire multiple times
		// if the element is moved in the DOM.
		if (this.__inited) return;
		this.__inited = true;

		// Read localized strings (set by Liquid on the nav-menu element).
		this.i18n = {
			searching: this.dataset.i18nSearching || "Searching...",
			error: this.dataset.i18nError || "Search unavailable.",
			prompt: this.dataset.i18nPrompt || "Search for products",
			foundOne: this.dataset.i18nFoundOne || "{count} product found.",
			foundOther: this.dataset.i18nFoundOther || "{count} products found.",
		};

		this.querySelectorAll("[hover]").forEach((el) => {
			const target = el.getAttribute("hover");
			if (!target) return;
			el.addEventListener("mouseover", () => {
				this.getAttribute("active") !== target && this.setNav(target);
			});
		});
		this.searchResults = this.querySelector("#search-results");
		this.searchStatus = this.querySelector("#search-status");
		this.input = this.querySelector("input[type=search]");
		this.shopbutton = this.querySelector("button#shopbutton");
		this.querySelector(".toggle")?.addEventListener("click", () =>
			this.setNav("links")
		);
		this.querySelector("button.search")?.addEventListener(
			"click",
			this.setSearching.bind(this)
		);
		this.shopbutton?.addEventListener("click", () => this.setNav("collection"));
		this.querySelector("nav#collection")?.addEventListener("mouseleave", () => {
			this.setNav("collection");
			document.documentElement.style.removeProperty("--collection-color");
		});
		this.querySelectorAll("nav#collection a").forEach((link) => {
			link.addEventListener("mouseover", () => {
				const color = link.style.getPropertyValue("--collection-color");
				document.documentElement.style.setProperty("--collection-color", color);
			});
		});
		this.querySelector("#overlay")?.addEventListener("click", () =>
			this.setNav(null)
		);
		this.input?.addEventListener(
			"input",
			this.debounce((event) => this.onSearch(event), 300).bind(this)
		);
		this.input?.addEventListener(
			"input",
			this.debounce((event) => this.tracking(event), 800).bind(this)
		);

		// Theme-switcher buttons (replaces inline onclick handlers).
		this.querySelectorAll("[data-theme-action]").forEach((btn) => {
			const action = btn.getAttribute("data-theme-action");
			btn.addEventListener("click", () => {
				if (!window.themeManager) return;
				if (action === "reset") {
					window.themeManager.resetToDefault();
				} else if (action) {
					window.themeManager.changeTheme(action);
				}
			});
		});
	}
	tracking(event) {
		const search_term = event.target.value;
		if (!search_term.length) return;
		if (typeof gtag !== "function") return;
		gtag("event", "search", {
			search_term,
		});
	}
	setSearching() {
		this.setNav("searching");
		this.querySelector("input[type=search]").value = "";
		this.setSearchResultsBlank();
		this.querySelector("input[type=search]").focus();
	}
	clearNavs() {
		document.body.removeAttribute("lockscroll");
		this.querySelectorAll("nav[open]")?.forEach((e) =>
			e.removeAttribute("open")
		);
	}
	static globalSetNav(nav) {
		const navbar = document.querySelector("nav-menu");
		if (navbar.children[nav]?.getAttribute("open")) {
			navbar.removeAttribute("active");
			navbar
				.querySelectorAll("nav[open]")
				?.forEach((e) => e.removeAttribute("open"));
			document.body.removeAttribute("lockscroll");
			return;
		}
		if (nav === null) return navbar.removeAttribute("active");
		document.body.setAttribute("lockscroll", "true");
		navbar.children[nav].setAttribute("open", "true");
		navbar.setAttribute("active", nav);
		return navbar.querySelector("nav[open]");
	}
	setNav(nav) {
		if (this.children[nav]?.getAttribute("open")) {
			this.removeAttribute("active");
			this.clearNavs();
			return;
		}
		this.clearNavs();
		if (nav === null) return this.removeAttribute("active");
		document.body.setAttribute("lockscroll", "true");
		this.children[nav].setAttribute("open", "true");
		this.setAttribute("active", nav);
	}
	getSearchResults(search_term) {
		// Cancel any in-flight request so a slower previous query can't overwrite a faster newer one.
		this.searchAbort?.abort();
		this.searchAbort = new AbortController();
		fetch(
			`/search/suggest.json?q=${encodeURIComponent(search_term)}&resources[type]=product&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title,body,product_type,tag`,
			{ signal: this.searchAbort.signal }
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.searchResults.innerHTML = "";
					this.searchStatus.textContent = this.i18n.error;
					throw error;
				}
				return response.json();
			})
			.then((data) => {
				const { products } = data.resources.results;
				console.log(products);
				this.searchResults.innerHTML = "";
				const filtered_products = products.filter(
					({ type, tags }) => type !== "Addon" && !tags.includes("hidden")
				);
				filtered_products.forEach((product) => this.generateCard(product));
				const template =
					filtered_products.length === 1
						? this.i18n.foundOne
						: this.i18n.foundOther;
				this.searchStatus.textContent = template.replace(
					/##|\{count\}/,
					filtered_products.length
				);
			})
			.catch((error) => {
				// Aborted requests aren't errors - just a newer query superseded this one.
				if (error.name === "AbortError") return;
				this.searchResults.innerHTML = "";
				this.searchStatus.textContent = this.i18n.error;
				throw error;
			});
	}
	generateCard(product) {
		const card = document.createElement("a");
		const title = document.createElement("h3");
		title.appendChild(document.createTextNode(product.title));
		card.title = product.title;
		card.href = product.url;
		const picture = document.createElement("picture");
		picture.classList.add("product-image");
		const createImgTag = ({ alt, height, width, url }) => {
			const image = document.createElement("img");
			image.src = url;
			image.alt = alt;
			image.width = width;
			image.height = height;
			return image;
		};
		const image = createImgTag(product.featured_image);
		picture.appendChild(image);
		const price = document.createElement("span");
		price.classList.add("money");
		price.appendChild(document.createTextNode(`$ ${product.price}`));
		card.appendChild(picture);
		card.appendChild(title);
		card.appendChild(price);
		card.setAttribute("data-id", product.id);
		this.searchResults.appendChild(card);
	}
	setSearchResultsBlank() {
		this.searchStatus.textContent = this.i18n.prompt;
		this.searchResults.innerHTML =
			this.querySelector("nav#collection").innerHTML;
		this.searchResults.classList.replace("products", "collections");
	}
	onSearch() {
		const searchTerm = this.input.value.trim();

		if (!searchTerm.length) {
			return this.setSearchResultsBlank();
		}
		this.searchResults.classList.replace("collections", "products");
		this.searchResults.innerHTML = "";
		this.searchStatus.textContent = this.i18n.searching;
		this.getSearchResults(searchTerm);
	}
	debounce(fn, wait) {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), wait);
		};
	}
}
customElements.define("nav-menu", NavBar);
